import { useTranslation } from "react-i18next";
import {
  AmountSettings,
  amountRules,
  checkAmount,
} from "../utils/amountRules";
import formatCurrency from "../utils/formatCurrency";

// The deposit amount's hint and refusal, both read from the service's own
// settings through the one shared helper. A service that declares none keeps
// the copy and the single minimum the deposit screens have always shown.
const useDepositAmount = (
  settings: AmountSettings | null | undefined,
  currency: string | undefined,
  // Each deposit field has always had its own hint and required message.
  legacy: { hintKey: string; requiredKey: string }
) => {
  const { t } = useTranslation();
  const rules = amountRules(settings);
  const money = (value: number) => formatCurrency(value, undefined, currency);

  const hint = !rules.configured
    ? t(legacy.hintKey)
    : rules.multiple !== null
    ? t("schoolfee.amount.settings_multiple_min", {
        multiple: money(rules.multiple),
        min: money(rules.min),
      })
    : t("schoolfee.amount.settings_min", { min: money(rules.min) });

  // Empty string: the amount passes every rule.
  const message = (amount: string): string => {
    switch (checkAmount(amount, rules)) {
      case "required":
        return t(legacy.requiredKey);
      case "invalid":
      case "below_min":
        return rules.configured
          ? t("schoolfee.amount.below_service_min", { min: money(rules.min) })
          : t("payment.form.errors.min_amount");
      case "not_multiple":
        return t("schoolfee.amount.not_multiple", {
          multiple: money(rules.multiple ?? 0),
        });
      case "above_max":
        return t("payment.form.errors.above_max", {
          max: money(rules.max ?? 0),
        });
      default:
        return "";
    }
  };

  return { rules, hint, message };
};

export default useDepositAmount;
